const splitOssImageCommands = (process: string) =>
  process
    .split("/")
    .filter(Boolean)
    .filter((segment) => segment !== "image");

const mergeOssImageProcess = (
  existingProcess: string | null,
  newProcess: string,
): string => {
  const nextCommands = splitOssImageCommands(newProcess);

  if (!existingProcess) {
    return `image/${nextCommands.join("/")}`;
  }

  const existingCommands = splitOssImageCommands(existingProcess).filter(
    (command) =>
      !command.startsWith("format,") && !command.startsWith("resize,"),
  );

  const preservedCommands = Array.from(new Set(existingCommands));
  const formatCommand = nextCommands.find((command) =>
    command.startsWith("format,"),
  );
  const resizeCommand = nextCommands.find((command) =>
    command.startsWith("resize,"),
  );
  const extraCommands = nextCommands.filter(
    (command) =>
      command !== formatCommand &&
      command !== resizeCommand &&
      !preservedCommands.includes(command),
  );

  return [
    "image",
    ...(formatCommand ? [formatCommand] : []),
    ...preservedCommands,
    ...extraCommands,
    ...(resizeCommand ? [resizeCommand] : []),
  ].join("/");
};

/**
 * 添加或合并 OSS 图片处理参数
 * @param url 原始 URL
 * @param newProcess 新的处理参数
 * @returns 处理后的 URL
 */
const addOssProcess = (url: string, newProcess: string): string => {
  const urlObj = new URL(url);
  const existingProcess = urlObj.searchParams.get("x-oss-process");

  urlObj.searchParams.set(
    "x-oss-process",
    mergeOssImageProcess(existingProcess, newProcess),
  );

  return urlObj.toString();
};

type ResizeOptions = {
  w?: number; // width
  h?: number; // height
  p?: number; // percentage
  q?: number; // quality
};

export const convertToThumbnail = (
  url: string | undefined,
  resizeOptions: number | ResizeOptions = 80,
) => {
  if (!url) {
    return url;
  }

  let resizeParam: string;

  if (typeof resizeOptions === 'number') {
    // 兼容原有的数字形式，表示百分比缩放
    resizeParam = `resize,p_${resizeOptions}`;
  } else {
    // 对象形式，支持 w, h, p 选项
    const params: string[] = [];
    if (resizeOptions.w !== undefined) {
      params.push(`w_${resizeOptions.w}`);
    }
    if (resizeOptions.h !== undefined) {
      params.push(`h_${resizeOptions.h}`);
    }
    if (resizeOptions.p !== undefined) {
      params.push(`p_${resizeOptions.p}`);
    }
    if (resizeOptions.q !== undefined) {
      params.push(`q_${resizeOptions.q}`);
    }

    if (params.length === 0) {
      // 如果没有提供任何参数，默认使用 80% 缩放
      resizeParam = "resize,p_80";
    } else {
      resizeParam = `resize,${params.join(',')}`;
    }
  }

  try {
    return addOssProcess(url, `image/format,webp/${resizeParam}`);
  } catch (error) {
    // 如果 URL 解析失败，回退到原始方法
    console.warn("Failed to parse URL for thumbnail conversion:", error);
    return `${url}?x-oss-process=image/format,webp/${resizeParam}`;
  }
};

export const convertToWebp = (url: string | undefined) => {
  if (!url) {
    return url;
  }

  try {
    return addOssProcess(url, "image/format,webp");
  } catch (error) {
    // 如果 URL 解析失败，回退到原始方法
    console.warn("Failed to parse URL for WebP conversion:", error);
    return `${url}?x-oss-process=image/format,webp`;
  }
};

export const isOssImageUrl = (url: string | undefined) => {
  if (!url) {
    return false;
  }

  try {
    const urlObj = new URL(url);
    return (
      urlObj.hostname === "oss-macaron-admin.macaron.im" ||
      urlObj.hostname.endsWith(".oss-accelerate.aliyuncs.com")
    );
  } catch {
    return false;
  }
};

type ResponsiveOssImageOptions = {
  widths: readonly number[];
  fallbackWidth: number;
  quality?: number;
};

export type ResponsiveOssImageSet = {
  src: string;
  srcSet: string;
};

const buildThumbnailForWidth = (
  url: string,
  width: number,
  quality?: number,
) => {
  const resizeOptions: ResizeOptions = { w: width };

  if (quality !== undefined) {
    resizeOptions.q = quality;
  }

  return convertToThumbnail(url, resizeOptions);
};

export const buildResponsiveOssImageSet = (
  url: string | undefined,
  options: ResponsiveOssImageOptions,
 ): ResponsiveOssImageSet | undefined => {
  if (!url || !isOssImageUrl(url)) {
    return undefined;
  }

  const fallbackWidth = Math.round(options.fallbackWidth);
  if (!Number.isFinite(fallbackWidth) || fallbackWidth <= 0) {
    return undefined;
  }

  const normalizedWidths = Array.from(
    new Set(
      [...options.widths, fallbackWidth]
        .map((value) => Math.round(value))
        .filter((value) => Number.isFinite(value) && value > 0),
    ),
  ).sort((a, b) => a - b);

  const fallbackSrc = buildThumbnailForWidth(url, fallbackWidth, options.quality);
  if (!fallbackSrc) {
    return undefined;
  }

  const srcSet = normalizedWidths
    .map((width) => {
      const candidate = buildThumbnailForWidth(url, width, options.quality);
      if (!candidate) {
        return undefined;
      }
      return `${candidate} ${width}w`;
    })
    .map((entry) => entry?.trim())
    .filter((entry): entry is string => typeof entry === "string" && entry.length > 0)
    .join(", ");

  if (!srcSet) {
    return undefined;
  }

  return { src: fallbackSrc, srcSet };
};
